/* eslint-disable max-lines */
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { FC, useEffect, useMemo, useState } from "react";
import { IEsimRealizedPackageMargin } from "../../../interfaces/esim/EsimMargin.types";
import { eurOrDash, gb, packageLabel } from "../../../utils/esim/margin";
import { foldSearchText } from "../../../utils/searchText";
import { WithTip } from "../../ui/InfoTip";
import NotfoundTable from "../../ui/NotfoundTable";
import Table, { Row } from "../../ui/table";
import Pagination from "../../ui/table/Pagination";

const PAGE_SIZE = 50;

const COUNTRY_TIP =
  "Shitjet e paketave të blera në periudhë kundrejt kostos që operatori na faturoi për përdorimin " +
  "e tyre, sipas shteteve. Kliko një shtet për të parë paketat e tij.";

const COLUMNS = [
  <WithTip key="country" label="Shteti / Paketa" tip={COUNTRY_TIP} />,
  "Shitur",
  "Shitje",
  "Kosto e përdorimit",
  "GB të përdorura",
  "Fitimi",
  "Fitimi %"
];

interface Props {
  packages: IEsimRealizedPackageMargin[];
  loading: boolean;
}

interface CountryGroup {
  key: number;
  name: string;
  packages: IEsimRealizedPackageMargin[];
  sold: number;
  giftCount: number;
  sales: number;
  usageCost: number;
  usedGb: number;
  margin: number;
  marginPercent: number | null;
  /** Found through one of its packages by the search, so it opens by itself. */
  matchedByPackage?: boolean;
}

const pct = (v: number | null) => (v === null ? "–" : `${v.toFixed(1)}%`);

const pctClass = (v: number | null) => {
  if (v === null) return "text-neutral";
  if (v < 0) return "text-red font-medium";
  if (v < 30) return "text-[#F59E0B] font-medium";
  return "text-confirm font-medium";
};

// Most sold first; the money they brought breaks a tie.
const bySales = (a: { sold: number; sales: number }, b: { sold: number; sales: number }) =>
  b.sold - a.sold || b.sales - a.sales;

const round2 = (v: number) => Math.round(v * 100) / 100;

const sum = (items: IEsimRealizedPackageMargin[], pick: (p: IEsimRealizedPackageMargin) => number) =>
  items.reduce((s, p) => s + pick(p), 0);

// A country row over the packages it lists, so the rows under it always add up to it.
const summarise = (key: number, name: string, items: IEsimRealizedPackageMargin[]): CountryGroup => {
  const sales = round2(sum(items, (p) => p.sales));
  const margin = round2(sum(items, (p) => p.margin));
  return {
    key,
    name,
    packages: [...items].sort(bySales),
    sold: sum(items, (p) => p.sold),
    giftCount: sum(items, (p) => p.giftCount),
    sales,
    usageCost: round2(sum(items, (p) => p.usageCost)),
    usedGb: round2(sum(items, (p) => p.usedGb)),
    margin,
    marginPercent: sales > 0 ? (margin / sales) * 100 : null
  };
};

// One row per country (the package's zone), its packages summed; the share is taken from the sums.
const groupByCountry = (packages: IEsimRealizedPackageMargin[]): CountryGroup[] => {
  const groups = new Map<number, IEsimRealizedPackageMargin[]>();
  packages.forEach((p) => groups.set(p.locationZoneId, [...(groups.get(p.locationZoneId) ?? []), p]));
  return Array.from(groups.entries())
    .map(([key, items]) =>
      summarise(key, items.find((p) => p.locationZoneName)?.locationZoneName ?? `Zona ${key}`, items)
    )
    .sort(bySales);
};

const soldCell = (sold: number, giftCount: number) => (
  <span>
    {sold}
    {giftCount > 0 && <span className="text-neutral text-xs"> ({giftCount} falas)</span>}
  </span>
);

const figures = (r: Omit<CountryGroup, "key" | "name" | "packages"> | IEsimRealizedPackageMargin) => ({
  sold: soldCell(r.sold, r.giftCount),
  sales: eurOrDash(r.sales),
  cost: eurOrDash(r.usageCost),
  usedGb: gb(r.usedGb),
  margin: <span className={pctClass(r.marginPercent)}>{eurOrDash(r.margin)}</span>,
  marginPct: <span className={pctClass(r.marginPercent)}>{pct(r.marginPercent)}</span>
});

const RealizedTable: FC<Props> = ({ packages, loading }) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<Set<number>>(new Set());

  const countries = useMemo(() => groupByCountry(packages), [packages]);

  // A search keeps a country found by its name whole, and a country found through its packages with only
  // those packages, summed over them and opened.
  const filtered = useMemo(() => {
    const term = foldSearchText(search);
    if (!term) return countries;
    return countries
      .map((c) => {
        if (foldSearchText(c.name).includes(term)) return c;
        const matching = c.packages.filter((p) => foldSearchText(p.packageName ?? "").includes(term));
        return { ...summarise(c.key, c.name, matching), matchedByPackage: true };
      })
      .filter((c) => c.packages.length > 0)
      .sort(bySales);
  }, [countries, search]);

  useEffect(() => {
    setPage(1);
    // Opened once per search, so the chevron still closes them.
    if (search.trim()) setOpen(new Set(filtered.filter((c) => c.matchedByPackage).map((c) => c.key)));
  }, [filtered]);

  const toggle = (key: number) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const rows = pageRows.flatMap((c) => {
    const expanded = open.has(c.key);
    const Chevron = expanded ? ChevronDownIcon : ChevronRightIcon;
    const country = (
      <Row
        key={`c-${c.key}`}
        name={
          <button
            type="button"
            onClick={() => toggle(c.key)}
            className="flex items-center gap-2 font-medium text-left"
            aria-expanded={expanded}
          >
            <Chevron className="w-4 h-4 shrink-0" />
            {c.name}
            <span className="text-neutral text-xs font-normal">{c.packages.length} paketa</span>
          </button>
        }
        {...figures(c)}
      />
    );
    if (!expanded) return [country];

    return [
      country,
      ...c.packages.map((p) => (
        <Row
          key={`p-${p.packageTemplateId}`}
          className="bg-gray-50"
          name={
            <span
              className="inline-block max-w-sm truncate align-bottom pl-6"
              title={p.packageName ?? String(p.packageTemplateId)}
            >
              {packageLabel(p.packageName) ?? `Paketa #${p.packageTemplateId}`}
            </span>
          }
          {...figures(p)}
        />
      ))
    ];
  });

  return (
    <>
      <div className="flex items-center justify-between gap-4 mb-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Kërko shtet ose paketë"
          className="w-full sm:w-72 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm
            focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white"
        />
        <span className="text-xs text-neutral whitespace-nowrap">{filtered.length} shtete</span>
      </div>
      <Table titles={COLUMNS} loading={loading} style="hover">
        {rows}
      </Table>
      {!loading && filtered.length === 0 && (
        <NotfoundTable header="Nuk ka shitje" paragraph="Asnjë paketë nuk u shit në këtë periudhë." />
      )}
      <Pagination
        totalCount={filtered.length}
        pageSize={PAGE_SIZE}
        page={page}
        length={pageRows.length}
        onPageChange={({ selected }) => setPage(selected + 1)}
      />
    </>
  );
};

export default RealizedTable;
